import axios from 'axios';

import {
  BroadcastDTO,
  CreateBroadcastDTO,
  SentBroadcastsDTO,
  ReceivedBroadcastsDTO,
} from '../models/BroadcastDTO';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';

export const broadcastApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/broadcast`,
  withCredentials: true,
});

installSessionInterceptors(broadcastApi);

const GENERIC_SEND_ERROR = 'Failed to send broadcast. Please try again.';

/**
 * Extract a human-readable message from an API error response. Handles
 * ASP.NET ProblemDetails (`detail`/`title`), plain-string bodies, and
 * loose `{ message }` shapes. Returns null when no usable text is found.
 */
const extractApiErrorMessage = (error: unknown): string | null => {
  if (!axios.isAxiosError(error)) return null;
  const data = error.response?.data;

  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const detail = typeof obj.detail === 'string' ? obj.detail.trim() : '';

    if (detail) return detail;
    const message = typeof obj.message === 'string' ? obj.message.trim() : '';

    if (message) return message;
    const title = typeof obj.title === 'string' ? obj.title.trim() : '';

    // `title` on ProblemDetails is usually the generic status phrase
    // ("Bad Request"); only use it if nothing better surfaced.
    if (title && title.toLowerCase() !== 'bad request') return title;
  }

  return null;
};

/**
 * Create a new broadcast message.
 *
 * Throws `Error` with the backend-provided message when the API returns
 * an error — callers should surface `err.message` to the user so causes
 * like "No recipients found for broadcast" are visible rather than
 * hidden behind a generic retry prompt.
 */
export const createBroadcast = async (request: CreateBroadcastDTO): Promise<BroadcastDTO> => {
  try {
    const response = await broadcastApi.post('', request);

    return response.data;
  } catch (error) {
    console.error('Error creating broadcast:', error);
    throw new Error(extractApiErrorMessage(error) ?? GENERIC_SEND_ERROR);
  }
};

/**
 * Get broadcasts sent by the current user
 */
export const getSentBroadcasts = async (): Promise<SentBroadcastsDTO> => {
  try {
    const response = await broadcastApi.get('/sent');

    return response.data;
  } catch (error) {
    console.error('Error getting sent broadcasts:', error);

    return { broadcasts: [], totalCount: 0 };
  }
};

/**
 * Get broadcasts received by the current user
 */
export const getReceivedBroadcasts = async (): Promise<ReceivedBroadcastsDTO> => {
  try {
    const response = await broadcastApi.get('/received');

    return response.data;
  } catch (error) {
    console.error('Error getting received broadcasts:', error);

    return { broadcasts: [], totalCount: 0, unreadCount: 0 };
  }
};

/**
 * Get a specific broadcast by ID
 */
export const getBroadcast = async (broadcastId: string): Promise<BroadcastDTO | null> => {
  try {
    const response = await broadcastApi.get(`/${broadcastId}`);

    return response.data;
  } catch (error) {
    console.error('Error getting broadcast:', error);

    return null;
  }
};

/**
 * Mark a broadcast as read
 */
export const markBroadcastAsRead = async (
  broadcastId: string,
  createdAt: string
): Promise<boolean> => {
  try {
    await broadcastApi.put(`/${broadcastId}/read`, null, {
      params: { createdAt },
    });

    return true;
  } catch (error) {
    console.error('Error marking broadcast as read:', error);

    return false;
  }
};
